// Slice 12 — BesideU Admin org drill-in (DESIGN §6.6). One screen per
// organisation: status, headcount, DPA, safety contact, HR Admin roster, and
// recent audit entries scoped to this org. Mutations match the row-level
// actions on the list view but render with full-width affordances here.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type {
  AdminAuditEntry,
  AdminHrAdminCandidate,
  AdminOrgSummary,
  UpdateOrgSettingsBody,
} from '@besideu/shared/admin';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { PoolSourceBadge } from '@/components/ui/PoolSourceBadge.tsx';

export function AdminOrgDetail() {
  // React Router always supplies `id` for the `/admin/orgs/:id` segment, but
  // `useParams` types it as optional. We feed `id ?? ''` into queryKeys and
  // gate the fetches with `enabled: !!id` so hook order stays stable even in
  // the theoretical `undefined` case — Rules of Hooks: no conditional returns
  // before hooks.
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const orgQuery = useQuery({
    queryKey: ['admin', 'orgs', id ?? ''],
    queryFn: () => api.get<{ org: AdminOrgSummary }>(`/api/admin/orgs/${id ?? ''}`),
    enabled: !!id,
  });
  const hrAdminsQuery = useQuery({
    queryKey: ['admin', 'orgs', id ?? '', 'hr-admins'],
    queryFn: () =>
      api.get<{ candidates: AdminHrAdminCandidate[] }>(`/api/admin/orgs/${id ?? ''}/hr-admins`),
    enabled: !!id,
  });
  const auditQuery = useQuery({
    queryKey: ['admin', 'audit-log'],
    queryFn: () => api.get<{ entries: AdminAuditEntry[] }>('/api/admin/audit-log?limit=100'),
  });

  const orgAudit = useMemo(
    () => (auditQuery.data?.entries ?? []).filter((e) => e.targetOrgId === id),
    [auditQuery.data, id],
  );

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
  }

  if (!id) return null;

  if (orgQuery.isPending) {
    return (
      <section className="space-y-4">
        <BackLink />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }
  if (orgQuery.error || !orgQuery.data) {
    return (
      <section className="space-y-4">
        <BackLink />
        <p className="text-sm text-traffic-red">Could not load this organisation.</p>
      </section>
    );
  }

  const org = orgQuery.data.org;

  return (
    <section className="space-y-8">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{org.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusPill status={org.status} />
            <span>·</span>
            <span>Created {new Date(org.createdAt).toLocaleDateString()}</span>
          </p>
        </div>
        <OrgActions org={org} onDone={invalidateAll} />
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="HR Admins" value={org.hrAdminCount} />
        <MetricCard label="Managers" value={org.managerCount} />
        <MetricCard label="Employees" value={org.employeeCount} />
        <MetricCard label="Open cases" value={org.caseCountOpen} />
        <MetricCard label="Closed cases" value={org.caseCountClosed} />
        <MetricCard
          label="DPA"
          value={org.dpaVersionAccepted ?? '—'}
          sublabel={
            org.dpaAcceptedAt
              ? `accepted ${new Date(org.dpaAcceptedAt).toLocaleDateString()}`
              : undefined
          }
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold">Safety contact</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {org.hrSafetyContactName
            ? `Currently ${org.hrSafetyContactName}.`
            : 'No safety contact assigned yet.'}
        </p>
        {org.pendingHrSafetyContactUserId ? (
          <p className="mt-1 text-sm text-traffic-amber">
            Pending acceptance by {org.pendingHrSafetyContactName ?? 'a nominated HR Admin'}
            {org.pendingHrSafetyContactNominatedAt
              ? ` since ${new Date(org.pendingHrSafetyContactNominatedAt).toLocaleDateString()}`
              : ''}
            .
          </p>
        ) : null}
        {org.status === 'Active' ? (
          <ReassignSafetyContactBlock
            orgId={id}
            currentUserId={org.hrSafetyContactUserId}
            pendingUserId={org.pendingHrSafetyContactUserId}
          />
        ) : null}
      </div>

      {/* PROJECT §4 Step 1 — onboarding captures. Read-only on the admin
          detail view: editing happens either at HR-side (signup / first-sign-in
          onboarding) or via a future admin-side mutation. */}
      <div>
        <h2 className="text-lg font-semibold">Onboarding</h2>
        <dl className="mt-3 grid gap-2 rounded-lg border border-border p-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs tracking-wide text-muted-foreground uppercase">
              Headcount band
            </dt>
            <dd className="mt-1 font-medium">{org.headcountBand ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs tracking-wide text-muted-foreground uppercase">
              Primary contact
            </dt>
            <dd className="mt-1 font-medium">
              {org.primaryContactName
                ? `${org.primaryContactName} (${org.primaryContactEmail})`
                : '—'}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs tracking-wide text-muted-foreground uppercase">
              Escalation contact
            </dt>
            <dd className="mt-1 font-medium">
              {org.escalationContactName
                ? `${org.escalationContactName} (${org.escalationContactEmail})`
                : '—'}
            </dd>
          </div>
        </dl>
      </div>

      <OrgSettingsBlock orgId={id} settings={org.settings} status={org.status} />

      <div>
        <h2 className="text-lg font-semibold">HR Admins</h2>
        {hrAdminsQuery.isPending ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : hrAdminsQuery.error ? (
          <p className="mt-2 text-sm text-traffic-red">Could not load HR Admins.</p>
        ) : (hrAdminsQuery.data?.candidates ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No HR Admins yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
            {hrAdminsQuery.data.candidates.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm"
              >
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.email}</p>
                </div>
                {c.isCurrentSafetyContact ? (
                  <span className="rounded-full bg-traffic-green/10 px-2 py-0.5 text-xs font-medium text-traffic-green-text">
                    Safety contact
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold">Recent activity</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          State-changing actions targeting this org (latest 100 across all tenants, filtered here).
        </p>
        {auditQuery.isPending ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
        ) : auditQuery.error ? (
          <p className="mt-3 text-sm text-traffic-red">Could not load audit log.</p>
        ) : orgAudit.length === 0 ? (
          <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No recent actions targeting this org.
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
            {orgAudit.map((e) => (
              <li key={e.id} className="px-4 py-3 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium">{formatAction(e.action)}</span>
                  <span className="text-xs whitespace-nowrap text-muted-foreground">
                    {new Date(e.createdAt).toLocaleString()}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {e.actorAdminName ??
                    (e.actorAdminId ? `${e.actorAdminId.slice(0, 8)}…` : 'System')}
                </p>
                {e.payload ? (
                  <pre className="mt-2 overflow-x-auto rounded bg-muted/40 px-2 py-1 text-xs">
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/orgs"
      className="text-sm text-muted-foreground underline hover:text-foreground"
    >
      ← Back to organisations
    </Link>
  );
}

function MetricCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: number | string;
  sublabel?: string | undefined;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
      {sublabel ? <p className="mt-1 text-xs text-muted-foreground">{sublabel}</p> : null}
    </div>
  );
}

function OrgActions({ org, onDone }: { org: AdminOrgSummary; onDone: () => void }) {
  const suspend = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/suspend`),
    onSuccess: onDone,
  });
  const reactivate = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/reactivate`),
    onSuccess: onDone,
  });
  const approve = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/approve`),
    onSuccess: onDone,
  });
  const reject = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/reject`),
    onSuccess: onDone,
  });

  function confirmThen(message: string, run: () => void) {
    if (typeof window !== 'undefined' && window.confirm(message)) run();
  }

  return (
    <div className="flex flex-wrap gap-2">
      {org.status === 'Pending' ? (
        <>
          <Button
            variant="primary"
            loading={approve.isPending}
            onClick={() =>
              confirmThen(`Approve ${org.name}? This activates the org.`, () => approve.mutate())
            }
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            loading={reject.isPending}
            onClick={() =>
              confirmThen(
                `Reject ${org.name}? This permanently deletes the pending org and its users.`,
                () => reject.mutate(),
              )
            }
          >
            Reject
          </Button>
        </>
      ) : null}
      {org.status === 'Active' ? (
        <Button
          variant="secondary"
          loading={suspend.isPending}
          onClick={() => confirmThen(`Suspend ${org.name}?`, () => suspend.mutate())}
        >
          Suspend
        </Button>
      ) : null}
      {org.status === 'Suspended' ? (
        <Button
          variant="primary"
          loading={reactivate.isPending}
          onClick={() => confirmThen(`Reactivate ${org.name}?`, () => reactivate.mutate())}
        >
          Reactivate
        </Button>
      ) : null}
    </div>
  );
}

function ReassignSafetyContactBlock({
  orgId,
  currentUserId,
  pendingUserId,
}: {
  orgId: string;
  currentUserId: string | null;
  pendingUserId: string | null;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ['admin', 'orgs', orgId, 'hr-admins'],
    queryFn: () =>
      api.get<{ candidates: AdminHrAdminCandidate[] }>(`/api/admin/orgs/${orgId}/hr-admins`),
    enabled: open,
  });

  const nominate = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${orgId}/safety-contact`, { userId: selected }),
    onSuccess: () => {
      setOpen(false);
      setSelected('');
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        setError('That user can’t take the role (not an HR Admin in this org, or not Active).');
      } else {
        setError('Something went wrong.');
      }
    },
  });

  const cancelNomination = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${orgId}/safety-contact/cancel`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
    onError: () => setError('Could not cancel the nomination. Try again.'),
  });

  const cancelButton = pendingUserId ? (
    <Button
      variant="ghost"
      className="mt-3 ml-2 px-2 py-1 text-xs"
      loading={cancelNomination.isPending}
      onClick={() => cancelNomination.mutate()}
    >
      Cancel nomination
    </Button>
  ) : null;

  if (!open) {
    return (
      <>
        <Button variant="ghost" className="mt-3 px-2 py-1 text-xs" onClick={() => setOpen(true)}>
          Nominate safety contact
        </Button>
        {cancelButton}
      </>
    );
  }

  if (candidates.isPending) {
    return <p className="mt-3 text-sm text-muted-foreground">Loading candidates…</p>;
  }
  if (candidates.error) {
    return <p className="mt-3 text-sm text-traffic-red">Could not load HR Admins.</p>;
  }

  const eligible = (candidates.data?.candidates ?? []).filter(
    (c) => c.id !== currentUserId && c.id !== pendingUserId,
  );
  if (eligible.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        No other Active HR Admins in this org. Invite a second HR Admin first via the customer’s HR
        shell.
      </p>
    );
  }

  return (
    <form
      className="mt-3 flex flex-wrap items-center gap-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (selected) nominate.mutate();
      }}
    >
      <label className="flex items-center gap-2">
        Nominate:
        <select
          className="rounded border border-border bg-background px-2 py-1"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          required
        >
          <option value="" disabled>
            Choose an HR Admin…
          </option>
          {eligible.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.email})
            </option>
          ))}
        </select>
      </label>
      <Button
        type="submit"
        variant="primary"
        className="px-2 py-1 text-xs"
        loading={nominate.isPending}
        disabled={!selected}
      >
        Nominate
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="px-2 py-1 text-xs"
        onClick={() => {
          setOpen(false);
          setSelected('');
          setError(null);
        }}
      >
        Cancel
      </Button>
      {error ? <span className="text-traffic-red">{error}</span> : null}
    </form>
  );
}

// Slice 33 — per-org settings editor. Today exposes only the
// `amberAggregationMinPool` override (Safety Guide Q7). The card shows
// the effective resolved value + a source badge (override / env /
// fallback) so the operator can tell at a glance whether the org is
// on the global default or a bespoke value. Edit mode swaps in a small
// numeric input plus "Clear override" to revert to the env-default
// path. Suspended orgs hide the Edit affordance (consistent with the
// safety-contact block's posture).
function OrgSettingsBlock({
  orgId,
  settings,
  status,
}: {
  orgId: string;
  settings: AdminOrgSummary['settings'];
  status: AdminOrgSummary['status'];
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    settings.amberAggregationMinPool.override !== null
      ? String(settings.amberAggregationMinPool.override)
      : '',
  );
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (body: UpdateOrgSettingsBody) =>
      api.patch<{ ok: true }>(`/api/admin/orgs/${orgId}/settings`, body),
    onSuccess: () => {
      // Sweeps both the detail page (`['admin', 'orgs', id]`) via prefix
      // match AND the list page (`['admin', 'orgs']` exact). One call
      // covers both — the Slice 33 post-review caught a second
      // invalidation here with the wrong key (`'org'` singular) that
      // didn't match any registered query and was misleading dead code.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      setEditing(false);
    },
  });

  const { override, effective, source } = settings.amberAggregationMinPool;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Settings</h2>
        {status === 'Active' && !editing ? (
          <Button
            variant="ghost"
            className="py-1 text-sm"
            onClick={() => {
              setDraft(override !== null ? String(override) : '');
              setEditing(true);
            }}
          >
            Edit
          </Button>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Per-org configuration values. Resolution order is per-org override → env-var default →
        hard-coded fallback (per <code>resolveAmberAggregationMinPool</code> in
        <code className="ml-1">apps/api/src/lib/org-settings.ts</code>).
      </p>

      <div className="mt-3 rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-base font-medium">Amber-aggregation pool minimum</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Minimum team size before an aggregate amber signal surfaces on the HR dashboard.
              Defends against identification-by-elimination in small teams (DESIGN §6.4, Safety
              Guide Q7).
            </p>
          </div>
          <PoolSourceBadge source={source} />
        </div>

        {!editing ? (
          <p className="mt-2 text-sm">
            Effective value: <span className="font-mono font-medium">{effective}</span>
            {override !== null ? (
              <span className="text-muted-foreground"> (per-org override)</span>
            ) : (
              <span className="text-muted-foreground"> (no per-org override)</span>
            )}
          </p>
        ) : (
          <form
            className="mt-3 grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = draft.trim();
              const value = trimmed === '' ? null : Number(trimmed);
              save.mutate({ amberAggregationMinPool: value });
            }}
          >
            <label className="block text-sm">
              <span className="text-xs tracking-wide text-muted-foreground uppercase">
                Per-org override (leave blank to clear)
              </span>
              <input
                type="number"
                min={1}
                max={10000}
                step={1}
                className="mt-1 block w-full max-w-xs rounded-md border border-border bg-background px-2 py-2 font-mono text-sm"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="(no override)"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={save.isPending}
                className="py-1.5 text-sm"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditing(false)}
                className="py-1.5 text-sm"
              >
                Cancel
              </Button>
            </div>
            {save.error ? (
              <p className="text-sm text-traffic-red" role="alert">
                {save.error instanceof ApiError && save.error.message === 'invalid_body'
                  ? 'Value must be an integer ≥ 1, or blank to clear the override.'
                  : 'Could not save. Try again.'}
              </p>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: AdminOrgSummary['status'] }) {
  const className =
    status === 'Active'
      ? 'bg-traffic-green/10 text-traffic-green-text'
      : status === 'Pending'
        ? 'bg-muted-foreground/15 text-foreground'
        : 'bg-traffic-red/10 text-traffic-red';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {status}
    </span>
  );
}

function formatAction(action: string): string {
  const lower = action.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
